import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Input } from "reactstrap";
import { ChangeEvent, useState } from "react";
import { findWhere } from "underscore";

import CollapsibleGroup from "../../cloud/CreateCloudView/CreateCloudComponents/CollapsibleGroup";
import CompanyInfoGroup from "../../cloud/CreateCloudView/CreateCloudGroups/CompanyInfoGroup";
import CloudUsersGroup from "../../cloud/CreateCloudView/CreateCloudGroups/CloudUsersGroup";
import CloudNameGroup from "../../cloud/CreateCloudView/CreateCloudGroups/CloudNameGroup";
import useDefaultPermissions from "../../../api/api-hooks/cloud/useDefaultPermissions";
import TierGroup from "../../cloud/CreateCloudView/CreateCloudGroups/TierGroup";
import { createCloud } from "../../cloud/CreateCloudView/CreateCloudView";
import useCreateCloud from "../../../api/api-hooks/cloud/useCreateCloud";
import useTemplates from "../../../api/api-hooks/cloud/useTemplates";
import AppDrawer from "../../../components/Drawers/AppDrawer";
import { newToastEvent } from "../../../utils/ToastUtil";
import AppSpinner from "../../../elements/AppSpinner";

interface CloudUser {
  Email: string;
  PermissionsID: string;
  TenantOwner: boolean;
}

interface CloudPayload {
  CompanyName: string;
  CompanyWebsite: string;
  BillingAddress: string;
  BillingAddress2: string;
  BillingCity: string;
  BillingState: string;
  BillingZip: string;
  BillingContactName: string;
  BillingContactEmail: string;
  Tier: "Team" | "Enterprise";
  Name: string;
  CloudUsers: CloudUser[];
  CRMTemplateID?: string;
}

const initialValues = () => {
  const initialValues: CloudPayload = {
    CompanyName: "",
    CompanyWebsite: "",
    BillingAddress: "",
    BillingAddress2: "",
    BillingCity: "",
    BillingState: "",
    BillingZip: "",
    BillingContactName: "",
    BillingContactEmail: "",
    Tier: "Team",
    Name: "",
    CloudUsers: [],
  };

  return initialValues;
};

interface Props {
  open: boolean;
  isSupportal?: boolean;
  onClose: () => void;
}

const CreateCloudDrawer = ({ open, isSupportal = false, onClose }: Props) => {
  const [values, setValues] = useState<CloudPayload>(initialValues());
  const [error, setError] = useState<string>();
  const queryClient = useQueryClient();
  const { defaultPermissions } = useDefaultPermissions();
  const { templates } = useTemplates();
  const { createCloud: createCloudAPI, creatingCloud } = useCreateCloud({
    isSupportal,
  });

  const handleOnClose = () => {
    setError(undefined);
    setValues(() => {
      return initialValues();
    });
    onClose();
  };

  return (
    <AppDrawer
      open={open}
      onClose={handleOnClose}
      headerTitle="Creating Cloud"
      size="md"
      headerOptions={
        <Button
          size="sm"
          color="primary"
          style={{ width: 90 }}
          disabled={
            !values?.BillingContactName ||
            !values?.BillingContactEmail ||
            !values?.Name ||
            !values.CloudUsers?.length ||
            !!creatingCloud
          }
          onClick={onCreateCloud}
        >
          {!!creatingCloud ? <AppSpinner color="white" /> : "Create"}
        </Button>
      }
      bodyCSS="pt-2"
      body={
        <>
          {error && (
            <Alert color="danger" className="py-2 px-4">
              {error}
            </Alert>
          )}
          <CollapsibleGroup
            title="Company Information"
            description="Enter your company's information here"
            startOpen={false}
          >
            <CompanyInfoGroup values={values} onChange={onChange} />
          </CollapsibleGroup>
          <CollapsibleGroup
            title="Select A Tier"
            description="Select a GrowthCloud hosting tier"
            startOpen={false}
          >
            <TierGroup values={values} onChange={onChange} />
          </CollapsibleGroup>
          <CollapsibleGroup
            title="Cloud Name"
            description="Choose a name for your new GrowthCloud"
            startOpen={false}
          >
            <CloudNameGroup values={values} onChange={onChange} />
          </CollapsibleGroup>
          <CollapsibleGroup
            title="Cloud Users"
            description="Invite team members to your new GrowthCloud"
            startOpen={false}
          >
            {/* @ts-ignore */}
            <CloudUsersGroup
              values={values}
              onChange={onChange}
              currentUser={null}
              defaultPermissions={defaultPermissions}
              useExistingUsers
            />
          </CollapsibleGroup>
          {error && (
            <Alert color="danger" className="py-2 px-4">
              {error}
            </Alert>
          )}
          <CollapsibleGroup
            title="Cloud Template"
            description="Use an existing template with predefined modules"
            startOpen={false}
            className="tw:mb-20"
          >
            <h5>CRM Template (Optional)</h5>
            <Input
              placeholder="Select A Template"
              type="select"
              name="CRMTemplateID"
              value={values?.CRMTemplateID ?? ""}
              onChange={onChange}
            >
              <option value="" disabled>
                Select A Template
              </option>
              {templates?.length > 0 &&
                templates.map((t: any, i: number) => (
                  <option key={i} value={t.id}>
                    {t.Name}
                  </option>
                ))}
            </Input>
          </CollapsibleGroup>
        </>
      }
    />
  );

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    setValues({ ...values, [e.target.name]: e.target.value });
  }

  function onCreateCloud() {
    const owner = findWhere(values.CloudUsers, { TenantOwner: true });

    if (!owner) {
      return setError(
        "Missing Cloud Owner. Ensure you add a user as the Owner."
      );
    }

    setError(undefined);

    createCloud({
      values,
      error,
      setError,
      ownerEmail: owner.Email,
      createCloudAPI,
      openCloud: false,
      isSupportal: isSupportal,
      onCreateCloudSuccess,
    });
  }

  function onCreateCloudSuccess() {
    queryClient.invalidateQueries({
      queryKey: ["allClouds"],
    });
    newToastEvent("success", "Successfully created cloud");
    setError(undefined);
    handleOnClose();
  }
};

export default CreateCloudDrawer;
